// @flow

/**
 * UniformLayoutEngine creates a grid of items that extends height of each item
 * in a row to be uniform.
 */

import MultiColumnLayoutEngine from 'app/common/react/components/Masonry9/MultiColumnLayoutEngine';
import { type buildLayoutProps } from 'app/common/react/components/Masonry9/LayoutEngine';
import Layout, {
  type Size,
  type ItemLayoutInfo,
} from 'app/common/react/components/Masonry9/Layout';

export default class UniformLayoutEngine<ItemType> extends MultiColumnLayoutEngine<ItemType> {
  buildLayout({
    containerSize,
    items,
    previousLayout,
  }: buildLayoutProps<ItemType>): Layout<ItemType> {
    // Get column width and count.
    const { width: colWidth, count: colCount } = this.getColumnInfo(containerSize);

    // Create a new layout with no layout info. We'll iterate over each item in
    // the previous layout (if specified) and copy over layout information.
    // $FlowFixMe: new errors found from flow 0.96 upgrade
    const newItemsLayoutInfo: WeakMap<ItemType, ItemLayoutInfo> = new WeakMap();

    // Initialize layout data.
    let overallHeight = 0;

    let itemsInRow: Array<{
      item: ItemType,
      itemLayoutInfo: ItemLayoutInfo,
    }> = [];
    let laidOutItems = 0;
    let rowHeight = 0;

    // Keep track of whether the layout needs measurements. If any pins need
    // measuring, we'll set this to true.
    let isMeasuring = false;

    // Keep track of the layout's overall size as we build it.
    const layoutSize: Size = {
      width: 0,
      height: 0,
    };

    // Keep track of the height + content height of the last filled row.
    let contentFilledHeight = 0;

    // Iterate over all items and add an entry in newLayout for each.
    for (let index = 0; index < items.length; index += 1) {
      const item = items[index];
      const itemLayoutInfo = previousLayout ? previousLayout.getItemLayoutInfo(item) : undefined;

      // If we have layout information for an item, we'll try to lay it out.
      // If not, we'll add it to the list to be measured.
      if (colCount > 0 && itemLayoutInfo && itemLayoutInfo.size) {
        // If we have layout info and the item doesn't need measuring, we
        // must have a width and height on the frame.
        const itemSize = itemLayoutInfo.size;
        if (!itemSize.width || !itemSize.height) {
          throw new Error('Items that have been measured should always have a width and height.');
        }

        // Add the item to the row.
        itemsInRow[laidOutItems % colCount] = { item, itemLayoutInfo };

        // Increase the height of the row to the max height of items in it.
        if (itemSize.height > rowHeight) {
          rowHeight = itemSize.height;
        }

        // If the row is full or we're on our last item, render the row.
        if (itemsInRow.length === colCount || index === items.length - 1) {
          // Iterate over each item in the row.
          for (let column = 0; column < itemsInRow.length; column += 1) {
            const rowEntry = itemsInRow[column];

            const { size: rowItemSize, constraints: rowItemConstraints } = rowEntry.itemLayoutInfo;
            if (!rowItemSize || !rowItemConstraints) {
              throw new Error('Row entries must already have sizes and constraints.');
            }

            newItemsLayoutInfo.set(rowEntry.item, {
              size: {
                width: colWidth,
                height: rowItemSize.height,
              },
              position: {
                left: column * (colWidth + this.gutter),
                top: overallHeight,
              },
              constraints: rowItemConstraints,
              customIndex: index,
            });
          }

          // If the row was full, keep track of its height. Up to this point,
          // this is the largest content area we've filled.
          if (itemsInRow.length === colCount) {
            contentFilledHeight = overallHeight + rowHeight;
          }

          // Update the overall height now that this row is rendered.
          overallHeight += rowHeight + this.gutter;

          // Reset the row.
          itemsInRow = [];
          rowHeight = 0;
        }

        laidOutItems += 1;
      } else {
        // Add the item to the list to be measured. Once it's rendered, we'll
        // update its width/height and set its status to needs-layout.
        newItemsLayoutInfo.set(item, {
          constraints: {
            width: colWidth,
          },
        });

        // We'll assume that this layout will be updated in the future with once
        // pins are measured.
        isMeasuring = true;
      }
    }

    const newLayout = new Layout(items, (item) => newItemsLayoutInfo.get(item));

    newLayout.size = layoutSize;
    newLayout.contentFilledSize = {
      width: layoutSize.width,
      height: contentFilledHeight,
    };
    newLayout.isMeasuring = isMeasuring;

    return newLayout;
  }
}
